/**
 * gstStates.ts — GST state-code reference + GSTIN parsing helpers.
 *
 * The GST portal (GSTR-1 JSON) and Tally both key place-of-supply by the
 * official two-digit state code that also forms the first two characters of
 * every GSTIN. This module is the single source of truth for that mapping
 * and for validating / parsing a GSTIN.
 *
 * Pure — no React, no DB, no I/O. Safe to unit-test in isolation.
 */
// ─── Official GST state / UT codes ───────────────────────────────────────────
// Source: CBIC state-code master. Codes are strings so leading zeros survive.
export const GST_STATE_CODES = {
    '01': 'Jammu and Kashmir',
    '02': 'Himachal Pradesh',
    '03': 'Punjab',
    '04': 'Chandigarh',
    '05': 'Uttarakhand',
    '06': 'Haryana',
    '07': 'Delhi',
    '08': 'Rajasthan',
    '09': 'Uttar Pradesh',
    '10': 'Bihar',
    '11': 'Sikkim',
    '12': 'Arunachal Pradesh',
    '13': 'Nagaland',
    '14': 'Manipur',
    '15': 'Mizoram',
    '16': 'Tripura',
    '17': 'Meghalaya',
    '18': 'Assam',
    '19': 'West Bengal',
    '20': 'Jharkhand',
    '21': 'Odisha',
    '22': 'Chhattisgarh',
    '23': 'Madhya Pradesh',
    '24': 'Gujarat',
    '25': 'Daman and Diu',
    '26': 'Dadra and Nagar Haveli and Daman and Diu',
    '27': 'Maharashtra',
    '28': 'Andhra Pradesh (Old)',
    '29': 'Karnataka',
    '30': 'Goa',
    '31': 'Lakshadweep',
    '32': 'Kerala',
    '33': 'Tamil Nadu',
    '34': 'Puducherry',
    '35': 'Andaman and Nicobar Islands',
    '36': 'Telangana',
    '37': 'Andhra Pradesh',
    '38': 'Ladakh',
    '97': 'Other Territory',
    '99': 'Centre Jurisdiction',
};
/** Reverse lookup: normalised state name → two-digit code. */
const NAME_TO_CODE = (() => {
    const map = {};
    for (const [code, name] of Object.entries(GST_STATE_CODES)) {
        map[normalizeStateName(name)] = code;
    }
    // Common aliases / spellings that differ from the canonical master.
    const aliases = {
        'pondicherry': '34',
        'orissa': '21',
        'uttaranchal': '05',
        'nct of delhi': '07',
        'new delhi': '07',
        'andaman nicobar': '35',
        'dadra nagar haveli': '26',
        'daman diu': '26',
        'jk': '01',
    };
    for (const [alias, code] of Object.entries(aliases))
        map[normalizeStateName(alias)] = code;
    return map;
})();
function normalizeStateName(name) {
    return name
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z]+/g, ' ')
        .trim();
}
// ─── GSTIN parsing / validation ──────────────────────────────────────────────
/**
 * Structural GSTIN validity: 15 chars — 2-digit state code, 10-char PAN,
 * 1 entity digit, fixed 'Z', 1 checksum char. Does NOT verify the checksum
 * digit (that requires the full GSTN algorithm) — this is the format gate
 * used to flag obviously-bad GSTINs before an export.
 */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export function isValidGstin(gstin) {
    if (!gstin)
        return false;
    const g = gstin.trim().toUpperCase();
    if (!GSTIN_REGEX.test(g))
        return false;
    // First two digits must be a real state code.
    return g.slice(0, 2) in GST_STATE_CODES;
}
/** Two-digit state code embedded in a GSTIN, or null if unparseable. */
export function stateCodeFromGstin(gstin) {
    if (!gstin)
        return null;
    const code = gstin.trim().slice(0, 2);
    return code in GST_STATE_CODES ? code : null;
}
/** Best-effort state name → two-digit code. Returns null if unrecognised. */
export function stateCodeFromName(name) {
    if (!name)
        return null;
    const key = normalizeStateName(name);
    if (!key)
        return null;
    if (NAME_TO_CODE[key])
        return NAME_TO_CODE[key];
    // Space-insensitive + contains match for values like "TAMILNADU" or
    // "Karnataka, India".
    const keyTight = key.replace(/ /g, '');
    for (const [n, code] of Object.entries(NAME_TO_CODE)) {
        if (n.length < 4)
            continue;
        if (keyTight === n.replace(/ /g, '') || key.includes(n))
            return code;
    }
    return null;
}
/** Human label for a state code, e.g. "33" → "33-Tamil Nadu". */
export function stateLabel(code) {
    if (!code)
        return '';
    const name = GST_STATE_CODES[code];
    return name ? `${code}-${name}` : code;
}
